import { User } from 'firebase/auth';
import { Timestamp } from 'firebase/firestore';
import { StreakData } from '../types';

// Development user configuration
export const DEV_USER_CONFIG = {
  // Enable dev mode with URL parameter: ?dev=true
  // Or set environment variable: REACT_APP_DEV_MODE=true
  isEnabled: () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('dev') === 'true' || process.env.REACT_APP_DEV_MODE === 'true';
  },

  // Get streak scenario from URL parameter: ?streak=missed, ?streak=active, ?streak=new
  getStreakScenario: () => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('streak') || 'active';
  },

  // Mock user data for development
  mockUser: {
    uid: 'dev-user-123',
    email: 'dev@example.com',
    displayName: 'Dev User',
    photoURL: 'https://via.placeholder.com/150/4F46E5/FFFFFF?text=DEV',
    emailVerified: true,
    isAnonymous: false,
    phoneNumber: null,
    providerId: 'google.com',
    metadata: {
      creationTime: new Date().toISOString(),
      lastSignInTime: new Date().toISOString(),
    },
    providerData: [],
    refreshToken: 'dev-refresh-token',
    tenantId: null,
    delete: async () => { },
    getIdToken: async () => 'dev-id-token',
    getIdTokenResult: async () => ({
      token: 'dev-id-token',
      authTime: new Date().toISOString(),
      issuedAtTime: new Date().toISOString(),
      expirationTime: new Date(Date.now() + 3600000).toISOString(),
      signInProvider: 'google.com',
      signInSecondFactor: null,
      claims: {},
    }),
    reload: async () => { },
    toJSON: () => ({}),
  } as User,

  // Mock streak data - dynamically generated based on scenario
  getMockStreakData: (): StreakData => {
    const scenario = DEV_USER_CONFIG.getStreakScenario();
    const today = new Date().toISOString().split('T')[0];

    switch (scenario) {
      case 'missed':
        // Simulate a missed streak - had 7 days, but missed yesterday
        const twoDaysAgo = new Date();
        twoDaysAgo.setDate(twoDaysAgo.getDate() - 2); // 2 days ago to trigger missed
        return {
          current: 0, // Streak broken
          longest: 7, // Previous best
          lastUpdated: new Date(),
          lastActivityDate: twoDaysAgo.toISOString().split('T')[0],
          milestones: [3, 7, 30, 100],
          milestonesReached: [3, 7], // Had reached these before
          celebrationShownToday: true, // Don't show celebration for broken streak
          streakMissedAcknowledged: false, // Show missed notification
        };

      case 'new':
        // Simulate starting a new streak
        return {
          current: 1, // Just started
          longest: 12, // Previous record
          lastUpdated: new Date(),
          lastActivityDate: today,
          milestones: [3, 7, 30, 100],
          milestonesReached: [], // No milestones yet
          celebrationShownToday: false, // Show celebration for new streak
          streakMissedAcknowledged: true,
        };

      case 'milestone':
        // Simulate reaching a milestone (7 days)
        return {
          current: 7, // Just hit weekly milestone
          longest: 12,
          lastUpdated: new Date(),
          lastActivityDate: today,
          milestones: [3, 7, 30, 100],
          milestonesReached: [3], // Just reached 7, so only 3 was previously reached
          celebrationShownToday: false, // Show milestone celebration
          streakMissedAcknowledged: true,
        };

      case 'active':
      default:
        // Default active streak
        return {
          current: 5, // 5-day streak for testing
          longest: 12,
          lastUpdated: new Date(),
          lastActivityDate: today,
          milestones: [3, 7, 30, 100],
          milestonesReached: [3], // Has reached 3-day milestone
          celebrationShownToday: false,
          streakMissedAcknowledged: true,
        };
    }
  },
  // Mock user document for Pro access
  mockUserDoc: {
    role: 'free' as const,
    entitlements: {
      pro: {
        status: 'expired' as const,
        stripeCustomerId: 'cus_dev123',
        subscriptionId: 'sub_dev123',
        expiresAt: Timestamp.fromDate(new Date(Date.now() - 365 * 24 * 60 * 60 * 1000)), // 1 year ago
      },
    },
    createdAt: Timestamp.now(),
    updatedAt: Timestamp.now(),
  } as UserDocument,
};

export interface UserDocument {
  role?: 'free' | 'plus' | 'pro';
  entitlements?: {
    pro?: {
      status: 'active' | 'cancelled' | 'expired';
      stripeCustomerId?: string;
      subscriptionId?: string;
      expiresAt?: any;
    };
  };
  createdAt: any;
  updatedAt: any;
}

// Helper to check if dev mode is active
export const isDevMode = () => DEV_USER_CONFIG.isEnabled();

// Helper to get dev user data
export const getDevUser = () => DEV_USER_CONFIG.mockUser;

// Helper to get dev streak data
export const getDevStreakData = () => DEV_USER_CONFIG.getMockStreakData();

// Helper to get dev user document
export const getDevUserDoc = () => DEV_USER_CONFIG.mockUserDoc;

// Console helper for dev mode
export const logDevMode = () => {
  if (isDevMode()) {
    const streakData = getDevStreakData();
    const scenario = DEV_USER_CONFIG.getStreakScenario();

    console.log('� Drevelopment Mode Active');
    console.log('� Diev User:', DEV_USER_CONFIG.mockUser.email);
    console.log('🔥 Dev Streak:', streakData.current, 'days');
    console.log('📊 Scenario:', scenario);
    console.log('💎 Pro Status: Active');
    console.log('💡 Tip: Add ?dev=false to URL to disable dev mode');
    console.log('🎯 Test scenarios: ?streak=missed, ?streak=new, ?streak=milestone, ?streak=active');
  }
};